/**
 * Scroll Hero — GSAP Animation Engine
 * 滚动叙事开屏动画引擎
 *
 * Dependencies: GSAP 3 + ScrollTrigger + Lenis
 *
 * Usage: Add data-scroll-hero="SECTION_ID" to the section wrapper.
 *        The script auto-initializes all instances on the page.
 */

(function () {
  'use strict';

  const isPc = window.innerWidth > 767;

  // ============================================================
  // Lenis — 全局平滑滚动（仅初始化一次）
  // ============================================================
  function initLenis() {
    if (window.__lenisInstance) return window.__lenisInstance;

    if (typeof Lenis === 'undefined') {
      console.warn('[ScrollHero] Lenis not loaded, using native scroll');
      return null;
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // 同步 GSAP ScrollTrigger
    lenis.on('scroll', function () {
      if (window.ScrollTrigger) {
        ScrollTrigger.update();
      }
    });

    // GSAP 集成：让 ScrollTrigger 使用 Lenis 的滚动位置
    if (window.ScrollTrigger) {
      window.ScrollTrigger.scrollerProxy(document.body, {
        scrollTop(value) {
          if (arguments.length) {
            lenis.scrollTo(value, { immediate: true });
          }
          return lenis.scroll;
        },
        getBoundingClientRect() {
          return {
            top: 0, left: 0,
            width: window.innerWidth,
            height: window.innerHeight
          };
        },
        pinType: document.body.style.transform ? 'transform' : 'fixed'
      });

      // 每次 Lenis 刷新后更新 ScrollTrigger
      lenis.on('scroll', ScrollTrigger.update);
    }

    window.__lenisInstance = lenis;
    return lenis;
  }

  // ============================================================
  // Scroll Hero — 单个实例的滚动动画
  // ============================================================
  function initScrollHero(section) {
    const sectionId = section.getAttribute('data-scroll-hero');
    if (!sectionId) return;

    // 获取动画参数（从 CSS 变量读取，由 section schema 注入）
    const styles = getComputedStyle(section);
    const scrollDist = styles.getPropertyValue('--scroll-distance').trim() || '400vh';
    const scrollDistMobile = styles.getPropertyValue('--scroll-distance-mobile').trim() || '300vh';
    const animDist = styles.getPropertyValue('--animation-distance').trim() || '200vh';
    const animDistMobile = styles.getPropertyValue('--animation-distance-mobile').trim() || '100vh';
    const logoStart = parseFloat(styles.getPropertyValue('--logo-scale-start').trim()) || 3;
    const logoEnd = parseFloat(styles.getPropertyValue('--logo-scale-end').trim()) || 1;
    const bgBrightness = parseFloat(styles.getPropertyValue('--bg-brightness-end').trim()) || 0.7;

    const bannerWrap = section.querySelector('.scroll-hero__banner-wrap');
    const captionText = section.querySelector('.scroll-hero__caption-text');
    const sloganText = section.querySelector('.scroll-hero__slogan');
    const logoEl = section.querySelector('.scroll-hero__logo svg, .scroll-hero__logo img');
    const bgImage = section.querySelector('.scroll-hero__bg-image');
    if (!bannerWrap) return;

    // 清理已有的 ScrollTrigger
    if (section.__scrollHeroST) {
      section.__scrollHeroST.forEach(function (st) { st.kill(); });
    }
    section.__scrollHeroST = [];

    const ctx = gsap.context(function () {
      // 预初始化 Logo 初始状态，避免首帧闪烁
      if (logoEl) {
        gsap.set(logoEl, {
          scale: isPc ? logoStart : Math.max(2, logoStart - 1),
          opacity: 0,
          filter: 'blur(10px)'
        });
      }

      // 主时间线（GSAP3 默认串行：文字渐隐 → Logo缩放+背景变暗）
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: bannerWrap,
          start: 'top top',
          end: isPc ? '+=' + animDist : '+=' + animDistMobile,
          scrub: 0.1,
        }
      });

      // ① Slogan + 标题文字同步渐隐（串行第一段）
      if (captionText) {
        tl.to(captionText, { opacity: 0, duration: 0.3 });
      }
      if (sloganText) {
        tl.to(sloganText, { opacity: 0, duration: 0.3 }, '<');
      }

      // ② Logo：由虚到实 + 由大到小（串行第二段）
      if (logoEl) {
        tl.to(logoEl, {
          scale: logoEnd,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.3
        });
      }

      // ③ 背景图片亮度降低（与 Logo 动画同步开始）
      if (bgImage) {
        tl.to(bgImage, { filter: 'brightness(' + bgBrightness + ')', duration: 0.3 }, '<');
      }

      section.__scrollHeroST.push(tl.scrollTrigger);
    }, section);

    section.__scrollHeroCtx = ctx;
  }

  // ============================================================
  // 初始化所有实例
  // ============================================================
  function initAll() {
    var sections = document.querySelectorAll('[data-scroll-hero]');
    if (sections.length === 0) return;

    // 先初始化 Lenis
    initLenis();

    // 延迟一帧确保 DOM 布局完成
    requestAnimationFrame(function () {
      sections.forEach(function (section) {
        initScrollHero(section);
      });
      initThermalSandbox();
    });
  }

  // ============================================================
  // 启动时机
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      // 等 GSAP 注册完毕
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
      }
      initAll();
    });
  } else {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }
    initAll();
  }

  // ============================================================
  // 热成像交互 — 移动端触摸支持
  // ============================================================
  function initThermalSandbox() {
    var sandboxes = document.querySelectorAll('[data-thermal-sandbox]');
    sandboxes.forEach(function (sandbox) {
      if (sandbox.__thermalInited) return;
      sandbox.__thermalInited = true;

      sandbox.addEventListener('touchstart', function () {
        sandbox.classList.add('is-active');
      }, { passive: true });

      sandbox.addEventListener('touchend', function () {
        sandbox.classList.remove('is-active');
      });

      sandbox.addEventListener('touchcancel', function () {
        sandbox.classList.remove('is-active');
      });
    });
  }

  // ============================================================
  // Shopify Theme Editor 支持（section 动态加载/卸载）
  // ============================================================
  if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var section = document.getElementById('shopify-section-' + e.detail.sectionId);
      if (section && section.querySelector('[data-scroll-hero]')) {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          gsap.registerPlugin(ScrollTrigger);
        }
        initAll();
        initThermalSandbox();
      }
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var section = document.getElementById('shopify-section-' + e.detail.sectionId);
      if (section) {
        var heroEl = section.querySelector('[data-scroll-hero]');
        if (heroEl && heroEl.__scrollHeroCtx) {
          heroEl.__scrollHeroCtx.revert();
        }
      }
    });
  }
})();
